import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, OnDestroy } from '@angular/core';

import { Store } from '@ngrx/store';
import { BehaviorSubject, filter, Observable, PartialObserver, share, Subscription, take, tap } from 'rxjs';

import { SentryLogger } from '@fcom/core';
import { OfferListFetchParams, TripType } from '@fcom/common/interfaces';
import { ConfigService } from '@fcom/core/services/config/config.service';
import { retryWithBackoff, snapshot } from '@fcom/rx';
import { unsubscribe } from '@fcom/core/utils';
import {
  FinnairAirBoundsRequest,
  FinnairAirBoundsRequestItinerary,
  FinnairAirBoundsRequestTravelers,
  FinnairAirBoundsResponse,
  FinnairItineraryItemFlight,
} from '@fcom/dapi/api/models';
import { InstantSearchMonitoring } from '@fcom/booking-widget/interfaces/store.interface';
import { instantSearchMonitoring } from '@fcom/booking-widget/store/selectors/booking-widget.selector';
import { BookingWidgetAndAppState } from '@fcom/booking-widget/store/selectors/booking-widget-feature-state.selector';
import { AirOffersStatus, BookingAppState, TicketSelectionParams } from '@fcom/common/interfaces/booking';
import { BookingWidgetActions } from '@fcom/booking-widget/store/actions';
import { NotificationWarning } from '@fcom/booking-widget/interfaces';
import { BoundsActions, globalBookingTripType } from '@fcom/common/store';
import { CommonAirBoundsService } from '@fcom/common/services';
import {
  airBoundsRequest,
  boundsStatus,
  lastRequestParams,
  selectedCheapestOffer,
  selectedInboundBound,
  serializedLastRequestParams,
} from '@fcom/common/store/selectors';
import { LoginStatus } from '@fcom/core-api/login';
import { DapiService } from '@fcom/dapi/api/dapi.service';
import { findAirBounds } from '@fcom/dapi/api/offer-functions';

import { mapAirBoundIdToBound, mapCityNameAndAirportToBounds } from '../../../utils';

const NO_FLIGHTS_STATUSES: string[] = ['NO_FLIGHTS_FOUND', 'NO_FLIGHTS_LEFT_FOR_TODAY'];
const DENIED_BY_AKAMAI = /You don't have permission to access/;

@Injectable()
export class BookingAirBoundsService implements OnDestroy {
  private configService = inject(ConfigService);
  private store$ = inject<Store<BookingAppState>>(Store);
  private bookingWidgetStore$ = inject<Store<BookingWidgetAndAppState>>(Store);
  private sentryLogger = inject(SentryLogger);
  private dapiService = inject(DapiService);
  private commonAirBoundsService = inject(CommonAirBoundsService);

  static NUMBER_OF_RETRIES = 1;
  NUMBER_OF_ONE_WAY_FLIGHT = 1;

  private subscription: Subscription;
  private upsellSubscription: Subscription;
  tripType$: Observable<TripType>;
  _preselectedBoundId$: BehaviorSubject<string> = new BehaviorSubject(null);

  constructor() {
    this.tripType$ = this.store$.pipe(globalBookingTripType());
  }

  get preselectedBoundId$(): Observable<string> {
    return this._preselectedBoundId$.asObservable();
  }

  ngOnDestroy(): void {
    this.subscription = unsubscribe(this.subscription);
    this.upsellSubscription = unsubscribe(this.upsellSubscription);
  }

  setPreselectedBoundId(boundId: string): void {
    this._preselectedBoundId$.next(boundId);
  }

  resetPreselectedBoundId(): void {
    if (this._preselectedBoundId$.getValue()) {
      this._preselectedBoundId$.next(null);
    }
  }

  /**
   * Triggers bound fetching for the given parameters. Stores the result
   * in the state tree under {@link AirBoundsState}
   */
  triggerFetchBounds(params: TicketSelectionParams, loginStatus: LoginStatus): void {
    if (this.subscription) {
      this.subscription = unsubscribe(this.subscription);
    }
    const serialized: string = snapshot(this.store$.pipe(serializedLastRequestParams()));
    const boundStatus: AirOffersStatus = snapshot(this.store$.pipe(boundsStatus()));
    const instantSearchMonitoringData: InstantSearchMonitoring = snapshot(
      this.bookingWidgetStore$.pipe(instantSearchMonitoring())
    );

    const currentParamsSerialized = JSON.stringify(params);

    // TODO: we need to fetch bounds again when they expire, even if the params stay the same
    if (boundStatus === AirOffersStatus.OK && serialized === currentParamsSerialized) {
      return;
    }

    this.store$.dispatch(BoundsActions.startLoadingOutbounds());
    this.store$.dispatch(BoundsActions.setLastRequestParams({ params }));

    this.subscription = this.commonAirBoundsService
      .fetchAndStoreOutboundsToCache(params, loginStatus)
      .pipe(
        take(1),
        tap(() => {
          const bodyParams = this.createRequest(params);
          if (params.flights.length === this.NUMBER_OF_ONE_WAY_FLIGHT) {
            Object.assign(bodyParams, instantSearchMonitoringData);
            this.store$.dispatch(
              BookingWidgetActions.setInstantSearchMonitoring({
                instantSearchMonitoring: { airCalendarPrice: undefined, instantSearchPrice: undefined },
              })
            );
          }
        })
      )
      .subscribe(this.handleBoundResponse(BoundsActions.setOutbounds));
  }

  /**
   * Retrieves the return bounds for a given outbound id.
   *
   * @param selectedOutboundId the selected outbound id
   * @param selectedFareFamilyCode the selected outbound fare family code
   */
  getReturnBounds(selectedOutboundId: string, selectedFareFamilyCode: string): void {
    const requestPayload = this.buildReturnBoundsRequest(selectedOutboundId, selectedFareFamilyCode);
    if (this.subscription) {
      this.subscription = unsubscribe(this.subscription);
    }
    this.store$.dispatch(BoundsActions.startLoadingInbounds());
    this.store$.dispatch(
      BookingWidgetActions.setInstantSearchMonitoring({
        instantSearchMonitoring: { airCalendarPrice: undefined, instantSearchPrice: undefined },
      })
    );
    this.subscription = this.getBounds(requestPayload)
      .pipe(retryWithBackoff(BookingAirBoundsService.NUMBER_OF_RETRIES))
      .subscribe(this.handleBoundResponse(BoundsActions.setInbounds));
  }

  /**
   * Fetches return bounds for the ECLASIC1 (classic) upgrade fare family in parallel to the
   * primary ESLIGHT1 inbound request. Stores the response in the NgRx store under `upsellBounds`
   * and auto-selects the matching classic inbound bound via `selectedUpsellBoundAirBoundId`.
   *
   * @param selectedOutboundId the ECLASIC1 outbound fare-family id (used as selectedBound.id in the request)
   * @param selectedFareFamilyCode e.g. 'ECLASIC1'
   */
  getReturnUpsellBounds(selectedOutboundId: string, selectedFareFamilyCode: string): void {
    const upsellRequestPayload = this.buildReturnBoundsRequest(selectedOutboundId, selectedFareFamilyCode);

    if (this.upsellSubscription) {
      this.upsellSubscription = unsubscribe(this.upsellSubscription);
    }

    this.upsellSubscription = this.getBounds(upsellRequestPayload)
      .pipe(retryWithBackoff(BookingAirBoundsService.NUMBER_OF_RETRIES))
      .subscribe({
        next: (data) => {
          if (NO_FLIGHTS_STATUSES.indexOf(data.status) === -1) {
            this.store$.dispatch(BoundsActions.setUpsellBounds({ bounds: mapCityNameAndAirportToBounds(data) }));
            this.preSelectUpsellInboundIfFound(data, selectedFareFamilyCode);
          }
          this.upsellSubscription = unsubscribe(this.upsellSubscription);
        },
        error: () => {
          this.upsellSubscription = unsubscribe(this.upsellSubscription);
        },
        complete: () => {
          this.upsellSubscription = unsubscribe(this.upsellSubscription);
        },
      });
  }

  /**
   * Dispatch action to set bound id if conditions met
   *
   * @param action kind of action
   * @param requestParams params from last request
   * @param bounds received bounds
   */
  handleFlightPreSelection(
    action: typeof BoundsActions.setOutbounds | typeof BoundsActions.setInbounds,
    bounds: FinnairAirBoundsResponse
  ): void {
    const requestParams = this.getLastRequestParams();
    const isValidParams = this.validateFlightPreSelectionParams(requestParams);
    const isTwoWay =
      isValidParams &&
      requestParams.flights?.length === 2 &&
      Boolean(requestParams.departureFlightNumbers) &&
      Boolean(requestParams.returnFlightNumbers);

    if (
      isValidParams &&
      action === BoundsActions.setOutbounds &&
      ((!isTwoWay && requestParams.departureFlightNumbers) || isTwoWay)
    ) {
      this.preSelectOutboundIfFound(bounds, requestParams);
    } else if (isValidParams && action === BoundsActions.setInbounds && isTwoWay) {
      this.preSelectInboundIfFound(bounds, requestParams);
    }
  }

  /**
   * Get last request params from store
   *
   * @returns {TicketSelectionParams} object
   */
  getLastRequestParams(): TicketSelectionParams {
    return snapshot(this.store$.pipe(lastRequestParams()));
  }

  private buildReturnBoundsRequest(
    selectedOutboundId: string,
    selectedFareFamilyCode: string
  ): FinnairAirBoundsRequest {
    const originalRequest: FinnairAirBoundsRequest = snapshot(this.store$.pipe(airBoundsRequest()));
    const isSelectedCheapestOffer: boolean = snapshot(this.store$.pipe(selectedCheapestOffer()));
    const instantSearchMonitoringData: InstantSearchMonitoring = snapshot(
      this.bookingWidgetStore$.pipe(instantSearchMonitoring())
    );

    if (isSelectedCheapestOffer) {
      Object.assign(originalRequest, instantSearchMonitoringData);
    }

    const itineraries = [
      Object.assign({}, originalRequest.itineraries[0], { isRequestedBound: false }),
      Object.assign({}, originalRequest.itineraries[1], { isRequestedBound: true }),
    ];

    return Object.assign({}, originalRequest, {
      itineraries,
      selectedBound: { id: selectedOutboundId, fareFamilyCode: selectedFareFamilyCode },
    });
  }

  /**
   * Get flight bounds for a certain date.
   *
   * @param params the search parameters to use
   * @returns {Observable<FinnairAirBoundsResponse>} the fetched bounds
   */
  private getBounds(params: FinnairAirBoundsRequest): Observable<FinnairAirBoundsResponse> {
    return this.dapiService
      .invoke$(findAirBounds, this.configService.cfg.offersUrl, {
        body: params,
      })
      .pipe(share());
  }

  private buildItineraries(params: OfferListFetchParams): FinnairAirBoundsRequestItinerary[] {
    const { directFlights, flights } = params;
    return flights.map((f, index) => ({
      directFlights,
      departureLocationCode: f.origin,
      destinationLocationCode: f.destination,
      departureDate: f.departureDate.toString(),
      isRequestedBound: index === 0,
    }));
  }

  /**
   * Create air bounds request from query parameters.
   *
   * @param params the search params used to create the request
   * @param params.locale the locale to send with the request
   * @param params.origin the IATA code of the destination to fly to
   * @param params.destination the IATA code of the departure location
   * @param params.directFlights
   * @param params.cabin the travel class to query
   * @param params.paxAmount passenger amounts
   * @param params.departureDate the date of departure
   * @param params.returnDate the date of return, leave undefined for one-way trips
   * @param params.promoCode given discount code to apply
   * @returns {FinnairAirBoundsRequest} the request object to use
   */
  private createRequest(params: OfferListFetchParams): FinnairAirBoundsRequest {
    const { cabin, locale, paxAmount, promoCode } = params;
    const itineraries = this.buildItineraries(params);
    return {
      locale,
      cabin,
      travelers: {
        adults: paxAmount.adults,
        children: paxAmount.children,
        c15s: paxAmount.c15s,
        infants: paxAmount.infants,
      } as unknown as FinnairAirBoundsRequestTravelers,
      itineraries,
      promoCode,
    };
  }

  private handleBoundResponse(
    action: typeof BoundsActions.setOutbounds | typeof BoundsActions.setInbounds
  ): PartialObserver<FinnairAirBoundsResponse> {
    return {
      next: (data) => {
        if (NO_FLIGHTS_STATUSES.indexOf(data.status) !== -1) {
          this.store$.dispatch(BoundsActions.noFlightsFound());
          this.store$.dispatch(
            BookingWidgetActions.setNotificationWarning({
              key: NotificationWarning.NO_FLIGHTS,
              isActive: true,
            })
          );
        } else {
          this.store$.dispatch(action({ bounds: mapCityNameAndAirportToBounds(data) }));

          this.handleFlightPreSelection(action, data);
        }
        this.subscription = unsubscribe(this.subscription);
      },
      error: (error: HttpErrorResponse) => {
        const status: string = error?.error?.status;

        if (status === 'INVALID_INPUT') {
          this.store$.dispatch(BoundsActions.invalidInput());
        } else {
          if (!DENIED_BY_AKAMAI.test(error.error)) {
            this.sentryLogger.error(`Error fetching bounds: ${error.status} ${error.statusText}`, { error });
          }
          this.store$.dispatch(BoundsActions.error());
        }
        this.subscription = unsubscribe(this.subscription);
      },
      complete: () => {
        this.subscription = unsubscribe(this.subscription);
      },
    };
  }

  private isValidFlightNumberFormat(value: string): boolean {
    // expects that the first 2 Characters are upper case letters
    // followed by 2 to 4 digits
    // ex. AY123
    return /^([A-Z]{2})(\d{2,4})$/.test(value);
  }

  private validateFlightPreSelectionParams(requestParams: TicketSelectionParams | undefined): boolean {
    if (!requestParams) {
      return false;
    }

    const { departureFlightNumbers, returnFlightNumbers } = requestParams;

    return [departureFlightNumbers, returnFlightNumbers].every((item) => {
      return item ? item.split(',').every((token) => this.isValidFlightNumberFormat(token.toUpperCase())) : true;
    });
  }

  private preSelectOutboundIfFound(bounds: FinnairAirBoundsResponse, requestParams: TicketSelectionParams): void {
    const { departureFlightNumbers, departureFareFamily } = requestParams;
    const airBoundId = mapAirBoundIdToBound(
      bounds,
      departureFlightNumbers.toUpperCase(),
      departureFareFamily?.toUpperCase()
    );
    if (airBoundId) {
      this.store$.dispatch(BoundsActions.setOutboundAirBoundId({ outboundAirBoundId: airBoundId }));
    }
  }

  private preSelectInboundIfFound(bounds: FinnairAirBoundsResponse, requestParams: TicketSelectionParams): void {
    const { returnFlightNumbers, returnFareFamily } = requestParams;
    const airBoundId = mapAirBoundIdToBound(bounds, returnFlightNumbers.toUpperCase(), returnFareFamily?.toUpperCase());
    if (airBoundId) {
      this.store$.dispatch(BoundsActions.setInboundAirBoundId({ inboundAirBoundId: airBoundId }));
    }
  }

  private preSelectUpsellInboundIfFound(bounds: FinnairAirBoundsResponse, selectedFareFamilyCode: string): void {
    this.store$.pipe(selectedInboundBound(), filter(Boolean), take(1)).subscribe((currentInboundBound) => {
      const flightNumbers = currentInboundBound?.details?.itinerary
        ?.map((i) => (i as FinnairItineraryItemFlight)?.flightNumber)
        .filter(Boolean)
        .join(', ');

      if (!flightNumbers) {
        return;
      }

      const upsellBoundId = mapAirBoundIdToBound(bounds, flightNumbers.toUpperCase(), selectedFareFamilyCode);

      if (upsellBoundId) {
        this.store$.dispatch(BoundsActions.setUpsellBoundAirBoundId({ upsellBoundAirBoundId: upsellBoundId }));
      }
    });
  }
}
